import React, { useState } from "react";
import { User, Mail, Phone, MapPin, Save, LogOut } from "lucide-react";
import { Alert } from "../../components/ui";
import BackButton from "../../components/BackButton";
import { ProfileAvatar } from "../../components/ProfilePhoto";
import { INDONESIAN_CITIES } from "../../data/cities";
import EmailVerification from "../../components/EmailVerification";
import AppRating from "../../components/AppRating";
import ReferralCard from "../../components/ReferralCard";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";
import { useNavigate } from "react-router-dom";

export default function CustomerProfile() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState(user?.profile?.display_name || "");
  const [city, setCity] = useState(user?.profile?.city || "");
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setErr("Format foto harus JPEG, PNG, atau WebP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErr("Ukuran foto maksimal 5MB.");
      return;
    }
    setPhotoBusy(true);
    setErr("");
    try {
      const form = new FormData();
      form.append("photo", file);
      await api.post("/api/profile/me/photo", form, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      setMsg("Foto profil diperbarui.");
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Gagal mengunggah foto.");
    } finally {
      setPhotoBusy(false);
    }
  };

  const deletePhoto = async () => {
    if (!window.confirm("Hapus foto profil?")) return;
    setPhotoBusy(true);
    try {
      await api.delete("/api/profile/me/photo");
      await refresh();
      setMsg("Foto profil dihapus.");
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Gagal menghapus foto.");
    } finally {
      setPhotoBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setMsg(null);
    setErr("");
    setSaving(true);
    try {
      await api.put("/api/profile/me", { display_name: displayName, city });
      await refresh();
      setMsg("Profil berhasil diperbarui.");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="animate-fade-up grid place-items-center py-8">
      <div className="container max-w-lg space-y-6">
        <BackButton to="/customer" />
        <div>
          <h1 className="text-brand-800">Profil Saya</h1>
          <p className="text-brand-400 mt-1">Kelola informasi akun Anda</p>
        </div>

        {/* Avatar + Name */}
        <div className="card p-6">
          <div className="flex items-center gap-4 mb-4">
            <ProfileAvatar
              userId={user?.id}
              photoUrl={user?.profile?.profile_photo_url}
              name={displayName}
              onSelect={uploadPhoto}
              inputTestId="profile-photo-input"
              disabled={photoBusy}
            />
            <div>
              <p className="font-bold text-brand-800 text-lg">{displayName || "Pelanggan"}</p>
              <span className="chip bg-brand-50 text-brand-600 mt-1">Pelanggan</span>
            </div>
          </div>
          <div className="flex gap-2 mb-6">
            <label className="btn-ghost text-sm cursor-pointer">
              {photoBusy ? "Mengunggah..." : "Ganti foto"}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                disabled={photoBusy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  uploadPhoto(f);
                }}
                data-testid="profile-photo-input-alt"
              />
            </label>
            {user?.profile?.profile_photo_url && (
              <button type="button" onClick={deletePhoto} disabled={photoBusy} className="btn-ghost text-sm text-red-600">
                Hapus foto
              </button>
            )}
          </div>

          <form onSubmit={save} className="space-y-4" data-testid="profile-form">
            {msg && <Alert kind="success" testid="profile-success">{msg}</Alert>}
            {err && <Alert testid="profile-error">{err}</Alert>}

            <div>
              <label className="field-label">Nama tampilan</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-300" />
                <input
                  className="field-input pl-12"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Nama Anda"
                  data-testid="profile-display-name"
                />
              </div>
            </div>

            <div>
              <label className="field-label">Kota</label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-300" />
                <input
                  className="field-input pl-12"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Kota Anda"
                  list="customer-city-list"
                  autoComplete="off"
                  data-testid="profile-city"
                />
              </div>
              <datalist id="customer-city-list">
                {INDONESIAN_CITIES.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>

            <div>
              <label className="field-label">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-300" />
                <input
                  className="field-input pl-12 bg-brand-50"
                  value={user?.email || ""}
                  disabled
                />
              </div>
              <p className="text-xs text-brand-400 mt-1">Email tidak dapat diubah</p>
            </div>

            <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="profile-save">
              <Save className="h-4 w-4" /> {saving ? "Menyimpan..." : "Simpan Perubahan"}
            </button>
          </form>
        </div>

        {/* Email Verification */}
        <div className="card p-6">
          <h2 className="text-brand-800 mb-3">Verifikasi Email</h2>
          <EmailVerification
            emailVerified={user?.email_verified}
            onVerified={() => refresh()}
          />
        </div>

        {/* App Rating */}
        <AppRating userId={user?.id} />

        {/* Referral */}
        <ReferralCard />

        {/* Logout */}
        <button onClick={doLogout} className="btn-ghost w-full text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300" data-testid="profile-logout">
          <LogOut className="h-4 w-4" /> Keluar dari Akun
        </button>
      </div>
    </div>
  );
}
